import {
  Heading,
  HStack,
  Stack,
  Text,
  VStack,
  Spinner,
  useBreakpointValue,
  useToast,
  IconButton,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback } from "react";
import { FaPlus } from "react-icons/fa";

import { EventsTable } from "./EventsTable";
import { EventsTableMobile } from "./EventsTableMobile";
import actions from "../../actions";
import { SearchClient, algoliasearch } from "algoliasearch";
import { useNavigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { useDialog } from "../../Common/Dialog";
import CreateEventModal, { CreateEventValues } from "./CreateEventModal";
import { useLoading } from "../hooks/form";
import { InstantSearch } from "react-instantsearch";
import { AlgoliaSortOrder } from "@common/interfaces/Algolia";
import { handleIndexNameTransform } from "src/utils";

enum EditModalType {
  INACTIVE,
  CREATE_EVENT,
}

const defaultState = {
  sortBy: "start_at",
  sortOrder: "ascending" as AlgoliaSortOrder,
};

export enum FilterType {
  UPCOMING = "upcoming",
  UNANNOUNCED = "unannounced",
  CANCELED = "canceled",
  PENDING_APPROVAL = "pending_approval",
  DRAFT = "draft",
  PAST = "past",
  ALL = "all",
}

const App = () => {
  const [searchClient, setSearchClient] = useState<SearchClient>();
  const [indexName, setIndexName] = useState<string>();
  const [loading, updateLoading] = useLoading({
    init: false,
    event: false,
  });
  const [modalType, setModalType] = useState(EditModalType.INACTIVE);
  const [sortOrder, setSortOrder] = useState<AlgoliaSortOrder>("ascending");
  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });
  const toast = useToast();
  const navigate = useNavigate();
  const dialog = useDialog();

  useEffect(() => {
    const fetchEvents = async () => {
      updateLoading("init", true);
      try {
        const response = await actions.ADMIN.fetchAlgoliaConfig();
        if (response.events_config) {
          const search = algoliasearch(
            response.events_config.app_id,
            response.events_config.api_key
          );
          search.clearCache();
          setSearchClient(search);
          setIndexName(response.events_config.index_name);
        }
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("init", false);
    };

    fetchEvents();
  }, [updateLoading, dialog]);

  const onCreateEvent = useCallback(
    async (data: CreateEventValues) => {
      updateLoading("event", true);
      try {
        const request = {
          name: data.name,
          subtitle: data.subtitle,
          display_mode: data.display_mode,
          age_restriction:
            data.age_restrictions_select === ""
              ? undefined
              : data.age_restrictions_select === "custom"
                ? data.age_restrictions_input
                : data.age_restrictions_select,
        };

        const result = await actions.ADMIN.createEvent(request);
        setModalType(EditModalType.INACTIVE);

        toast({
          title: "✅ Event Created",
          description: "Event has been created",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        navigate(`/events/${result.event_id}`);
      } catch (err) {
        dialog({ text: err.message });
      }
      updateLoading("event", false);
    },
    [updateLoading, dialog, navigate, toast]
  );

  const breakpointValue = useBreakpointValue({ base: "xs", lg: "sm" });

  const onChangeSort = (sortOrder: AlgoliaSortOrder) => {
    const newIndexName = handleIndexNameTransform(
      indexName,
      "start_at",
      sortOrder,
      defaultState
    );
    setIndexName(newIndexName);
    setSortOrder(sortOrder);
  };

  return (
    <Stack spacing={{ base: "4", lg: "6" }}>
      {searchClient ? (
        <InstantSearch
          indexName={indexName}
          searchClient={searchClient}
          initialUiState={{
            [indexName]: {
              menu: {
                status: "OPEN",
              },
            },
          }}
        >
          <CreateEventModal
            isLoading={loading["event"]}
            onClose={() => {
              setModalType(EditModalType.INACTIVE);
            }}
            onSave={(data) => {
              onCreateEvent(data);
            }}
            isOpen={modalType === EditModalType.CREATE_EVENT}
          />
          <Stack
            spacing="4"
            direction={{ base: "column", lg: "row" }}
            justify="space-between"
            align={{ base: "start", lg: "center" }}
          >
            <HStack spacing="1" width={"full"} justifyContent={"space-between"}>
              <Heading size={breakpointValue} fontWeight="medium">
                Events
              </Heading>
              <IconButton
                // isDisabled={status === "loading"}
                variant={"outline"}
                aria-label="Create event"
                icon={<FaPlus />}
                onClick={() => setModalType(EditModalType.CREATE_EVENT)}
              />
            </HStack>
          </Stack>
          {loading["init"] && (
            <VStack>
              <Spinner />
              <Text>Loading events...</Text>
            </VStack>
          )}

          <Stack
            h="full"
            w="full"
            flexGrow={1}
            spacing={{ base: "5", lg: "6" }}
          >
            {isMobile ? (
              <EventsTableMobile onChangeSort={onChangeSort} />
            ) : (
              <EventsTable onChangeSort={onChangeSort} sortOrder={sortOrder} />
            )}
          </Stack>
        </InstantSearch>
      ) : null}
    </Stack>
  );
};

export default App;
